/**
 * SpeedmasterCore.Shopping.js — Shopping entry point
 *
 * Handles images, search, and product display customisations.
 * Uses Layout.addToViewContextDefinition() for migrated modules.
 * Legacy modules (model/keymapping/url overrides) self-execute on define().
 *
 * Loyalty/rewards context moved to SpeedmasterLoyalty.Shopping.js.
 */
define('SpeedmasterCore.Shopping', [
    'SpeedmasterCore.Config',
    'SpeedmasterCore.Header',
    'SpeedmasterCore.PDP.Condition.View',
    'SpeedmasterCore.Favicon',
    'SpeedmasterCore.WebFont',
    'SC.Configuration',

    // Legacy modules — self-executing, apply prototype mutations on load.
    // No API equivalent exists for these patterns.
    'Item.KeyMapping.Ext',
    'Item.Model.Ext',
    'Product.Model.Ext',
    'ProductLine.Common.Image.Ext',
    'ProductDetails.ImageGallery.View.Ext',
    'Facets.ItemCell.View.Ext',
    'SpeedmasterCore.ScrollCarousel',
    'SpeedmasterCore.ScrollReveal',
    'SpeedmasterCore.Lightbox',
    'SpeedmasterCore.TemplateOverrides',
    'SpeedmasterCore.FacetSearch'
], function(Config, Header, ConditionView, Favicon, WebFont, Configuration) {
    'use strict';

    /* ------------------------------------------------------------------ */
    /*  Stock message helpers                                              */
    /* ------------------------------------------------------------------ */

    /**
     * Classify stock status from the view context.
     * Replaces jQuery :contains() string matching.
     * @param {Object} context — ProductLine.Stock.View context POJO
     * @returns {string} 'instock'|'buildtoorder'|'backorder'|'outofstock'|'unavailable'|'unknown'
     */
    function classifyStock(context) {
        if (context.isNotAvailableInStore) { return 'unavailable'; }
        if (context.showInStockMessage)    { return 'instock'; }
        if (context.showOutOfStockMessage) {
            var msg = (context.stockInfo && context.stockInfo.outOfStockMessage || '').toLowerCase();
            if (msg.indexOf('build to order') !== -1) { return 'buildtoorder'; }
            if (msg.indexOf('backorder') !== -1 || msg.indexOf('back order') !== -1) { return 'backorder'; }
            return 'outofstock';
        }
        // Fallback: base SC may not set showInStockMessage but stockInfo.isInStock is true
        if (context.stockInfo && context.stockInfo.isInStock) { return 'instock'; }
        return 'unknown';
    }

    /**
     * Resolve shipping time from config defaults per stock type.
     * @param {string} stockType
     * @param {Object} cfg — cached config object
     * @returns {string}
     */
    function getShippingTime(stockType, cfg) {
        switch (stockType) {
            case 'instock':      return cfg.inStockShipTime || '';
            case 'buildtoorder': return cfg.buildToOrderShipTime || '';
            case 'backorder':    return cfg.backorderShipTime || '';
            case 'outofstock':   return cfg.outOfStockShipTime || '';
            default:             return '';
        }
    }

    /* ------------------------------------------------------------------ */
    /*  mountToApp                                                         */
    /* ------------------------------------------------------------------ */

    return {
        mountToApp: function mountToApp(container) {
            /** @type {LayoutComponent} */
            var layout = container.getComponent('Layout');

            if (!layout) { return; }

            /* ========================================================== */
            /*  FAVICON — modern favicon injection                        */
            /* ========================================================== */

            Favicon.inject();
            WebFont.inject();

            /* ========================================================== */
            /*  SHARED HEADER — mega menu, search, logo, translations     */
            /* ========================================================== */

            Header.register(layout);

            /* ========================================================== */
            /*  PDP CONDITION PANEL (absorbed from PDPAdditions)           */
            /* ========================================================== */

            var pdp = container.getComponent('PDP');

            if (pdp) {
                pdp.addChildViews(pdp.PDP_FULL_VIEW, {
                    'StockDescription': function() {
                        return new ConditionView({ pdp: pdp });
                    }
                });
            }

            /* ========================================================== */
            /*  MIGRATED MODULES — addToViewContextDefinition              */
            /* ========================================================== */

            // ── Facets.ItemCell.View (3 properties) ─────────────────────
            // Legacy module: Facets.ItemCell.View.Ext.js
            // Uses _.wrap(getContext) because base view doesn't expose
            // 'model' in context — addToViewContextDefinition can't
            // access custom item fields (custitem_cseg_brand, etc.).

            // ── ProductLine.Sku.View — extendedWarrantyAvailable REMOVED ──
            // Was injecting custitem_extendedwarranty into SKU context for
            // the old template-based warranty badge. No template uses it now —
            // SpeedmasterWarranty.Cart.LineBadge.js handles cart badges via
            // DOM injection, PDP uses its own child view.

            // ── GlobalViews.Message.View (1 property — overrides) ───────
            // Replaces: GlobalViews.Message.View.Ext.js

            layout.addToViewContextDefinition(
                'GlobalViews.Message.View', 'message', 'string',
                function(context) {
                    return context.message ? Config.decodeHtml(context.message) : '';
                }
            );

            /* ========================================================== */
            /*  THUMBNAIL OVERRIDES (already addToViewContextDefinition)   */
            /*  Carried forward from SSS2.Shopping.EntryPoint.js           */
            /* ========================================================== */

            layout.addToViewContextDefinition(
                'Cart.Lines.View', 'thumbnail', 'object',
                function(context) {
                    return {
                        url: context.item
                            ? (context.item.custitemitemimageurl1 || (context.thumbnail && context.thumbnail.url) || '')
                            : (context.thumbnail && context.thumbnail.url) || '',
                        altimagetext: context.thumbnail ? context.thumbnail.altimagetext : ''
                    };
                }
            );

            // MiniCart thumbnail handled by Header.register() above

            layout.addToViewContextDefinition(
                'Cart.Confirmation.View', 'thumbnail', 'object',
                function(context) {
                    return {
                        url: context.model && context.model.item
                            ? (context.model.item.custitemitemimageurl1 || (context.thumbnail && context.thumbnail.url) || '')
                            : (context.thumbnail && context.thumbnail.url) || '',
                        altimagetext: context.thumbnail ? context.thumbnail.altimagetext : ''
                    };
                }
            );

            /* ========================================================== */
            /*  STOCK MESSAGE ENRICHMENT — ProductLine.Stock.View          */
            /*  Replaces jQuery :contains() hacks in 4 theme templates.    */
            /*  Injects stockType + shippingTime properties.               */
            /* ========================================================== */

            var stockCfg = {
                inStockShipTime:      Configuration.get('speedmaster.InStockShipTime') || '',
                buildToOrderShipTime: Configuration.get('speedmaster.BuildToOrderShipTime') || '',
                backorderShipTime:    Configuration.get('speedmaster.BackorderShipTime') || '',
                outOfStockShipTime:   Configuration.get('speedmaster.OutOfStockShipTime') || ''
            };

            layout.addToViewContextDefinition(
                'ProductLine.Stock.View', 'stockType', 'string',
                function(context) {
                    return classifyStock(context);
                }
            );

            layout.addToViewContextDefinition(
                'ProductLine.Stock.View', 'shippingTime', 'string',
                function(context) {
                    return getShippingTime(classifyStock(context), stockCfg);
                }
            );

            layout.addToViewContextDefinition(
                'ProductLine.Stock.View', 'showShippingTime', 'boolean',
                function(context) {
                    return getShippingTime(classifyStock(context), stockCfg).length > 0;
                }
            );

            layout.addToViewContextDefinition(
                'ProductLine.Stock.View', 'showInStock', 'boolean',
                function(context) {
                    return classifyStock(context) === 'instock';
                }
            );

            /* ========================================================== */
            /*  FREE SHIPPING — ProductDetails.Full.View                   */
            /*  Shows "Free Shipping" or "Free shipping on orders over $X" */
            /*  based on item price vs configurable threshold.             */
            /* ========================================================== */

            var freeShipThreshold = Configuration.get('speedmaster.FreeShippingThreshold') || 150;

            layout.addToViewContextDefinition(
                'ProductDetails.Full.View', 'freeShippingEligible', 'boolean',
                function(context) {
                    var model = context.model;
                    if (!model) { return false; }
                    var item = (typeof model.get === 'function') ? model.get('item') : model.item;
                    if (!item) { return false; }
                    // Price lives at item.onlinecustomerprice_detail.onlinecustomerprice
                    var priceDetail = item.onlinecustomerprice_detail;
                    var price = (priceDetail && priceDetail.onlinecustomerprice)
                        ? parseFloat(priceDetail.onlinecustomerprice) : 0;
                    return price >= freeShipThreshold;
                }
            );

            layout.addToViewContextDefinition(
                'ProductDetails.Full.View', 'freeShippingThreshold', 'number',
                function() {
                    return freeShipThreshold;
                }
            );

            /* ========================================================== */
            /*  PURCHASABLE CHECK — ProductDetails.Full.View               */
            /*  False when item is truly out of stock (Add to Cart gone).  */
            /*  Used by template to hide shipping + condition card.        */
            /* ========================================================== */

            layout.addToViewContextDefinition(
                'ProductDetails.Full.View', 'isPurchasable', 'boolean',
                function(context) {
                    var model = context.model;
                    if (!model) { return true; }
                    var item = (typeof model.get === 'function') ? model.get('item') : model.item;
                    if (!item) { return true; }
                    // ispurchasable is false when item cannot be added to cart
                    if (item.ispurchasable === false || item.ispurchasable === 'F') { return false; }
                    return true;
                }
            );

            /* ========================================================== */
            /*  AI PRODUCT ASSISTANT — ProductDetails.Full.View            */
            /*  Dynamic ChatGPT link with product name, brand, and URL.    */
            /*  Controlled by speedmaster.AIAssistantEnabled config.        */
            /* ========================================================== */

            var aiEnabled = Configuration.get('speedmaster.AIAssistantEnabled') !== false;

            layout.addToViewContextDefinition(
                'ProductDetails.Full.View', 'aiAssistantEnabled', 'boolean',
                function() {
                    return aiEnabled;
                }
            );

            layout.addToViewContextDefinition(
                'ProductDetails.Full.View', 'aiAssistantUrl', 'string',
                function(context) {
                    if (!aiEnabled) { return ''; }

                    var model = context.model;
                    var itemName = (model && model.item && model.item._name) || '';
                    var brand = (model && model.item && model.item.custitem_cseg_brand) || '';
                    var itemUrl = context.itemUrl || '';

                    // Always use production URL so ChatGPT reads the live listing
                    var fullUrl = 'https://www.speedmaster79.com' + itemUrl;

                    var prompt = 'You are an experienced automotive technician helping a customer'
                        + ' who just purchased this product from Speedmaster: ' + fullUrl
                        + '\n\nFirst, carefully read the product page and extract:'
                        + '\n- Product title and brand'
                        + '\n- Application / vehicle fitment'
                        + '\n- Technical specifications table'
                        + '\n- Any installation notes or requirements listed'
                        + '\n\nThen write a practical guide with these sections'
                        + ' (skip any section that does not apply to this product type):'
                        + '\n1. What\'s Included - list components based on the listing'
                        + '\n2. Before You Start - tools needed, safety precautions, vehicle prep'
                        + '\n3. Installation - step-by-step based on the specs and fitment from the page'
                        + '\n4. Break-In / First Use - if applicable'
                        + '\n5. Maintenance Tips'
                        + '\n6. Troubleshooting - common issues'
                        + '\n\nRules:'
                        + '\n- Only use information found on the product page or well-established automotive practice'
                        + '\n- If the page lacks detail for a section, say so rather than guessing'
                        + '\n- Use numbered steps and clear formatting'
                        + '\n- Reference the actual specs from the listing (dimensions, torque values, etc.)';

                    return 'https://chatgpt.com/?q=' + encodeURIComponent(prompt);
                }
            );

            // Search, logo, badge, categories, minicart thumbnail
            // all handled by Header.register(layout) above.

            /* ========================================================== */
            /*  ADD-TO-CART BUTTON STATE — Cart component                  */
            /*  "Adding to Cart…" → "Added to Cart ✓" → revert.          */
            /*  Suppresses confirmation modal — button IS the feedback.    */
            /* ========================================================== */

            var cart = container.getComponent('Cart');

            if (cart) {
                var _addTimer = null;
                var _revertTimer = null;

                /**
                 * Revert button to its original text and state.
                 * Called on success (after 2s) or failsafe timeout.
                 */
                function revertButton() {
                    var btn = document.querySelector('[data-type="add-to-cart"]');
                    if (!btn) { return; }
                    var original = btn.getAttribute('data-original-text') || 'Add to Cart';
                    btn.textContent = original;
                    btn.disabled = false;
                    btn.classList.remove('sm-btn-adding');
                    btn.classList.remove('sm-btn-added');
                    btn.removeAttribute('data-original-text');
                    // Remove modal suppression class
                    document.body.classList.remove('sm-suppress-modal');
                }

                cart.on('beforeAddLine', function onBeforeAddLine() {
                    // Clear any previous revert timer (rapid clicks)
                    if (_revertTimer) { clearTimeout(_revertTimer); _revertTimer = null; }

                    var btn = document.querySelector('[data-type="add-to-cart"]');
                    if (!btn) { return; }

                    // Suppress modal backdrop via body class (CSS hides .modal-backdrop)
                    document.body.classList.add('sm-suppress-modal');

                    // Store original text for revert
                    if (!btn.getAttribute('data-original-text')) {
                        btn.setAttribute('data-original-text', btn.textContent.replace(/^\s+|\s+$/g, ''));
                    }

                    btn.innerHTML = '<span class="sm-dot"></span><span class="sm-dot"></span><span class="sm-dot"></span>';
                    btn.disabled = true;
                    btn.classList.add('sm-btn-adding');

                    // Failsafe: revert if afterAddLine never fires (network error, etc.)
                    _addTimer = setTimeout(function() {
                        revertButton();
                    }, 8000);
                });

                cart.on('afterAddLine', function onAfterAddLine() {
                    // Clear failsafe timer
                    if (_addTimer) { clearTimeout(_addTimer); _addTimer = null; }

                    var btn = document.querySelector('[data-type="add-to-cart"]');
                    if (!btn) { return; }

                    var isUpdate = btn.getAttribute('data-original-text') === 'Update';
                    btn.textContent = isUpdate ? 'Updated \u2713' : 'Added to Cart \u2713';
                    btn.disabled = false;
                    btn.classList.remove('sm-btn-adding');
                    btn.classList.add('sm-btn-added');

                    // Clean up cart confirmation modal (CSS hides it instantly,
                    // but SC still adds it to DOM + backdrop + body.modal-open).
                    // Remove elements and restore body scroll.
                    setTimeout(function() {
                        if (typeof jQuery !== 'undefined') {
                            jQuery('[id="modal-Cart.Confirmation.View"]').remove();
                            jQuery('.modal-backdrop').remove();
                            jQuery('body').removeClass('modal-open').css('padding-right', '');
                        }
                    }, 100);

                    // Revert button after 2 seconds
                    _revertTimer = setTimeout(function() {
                        revertButton();
                    }, 2000);
                });

                /* ------------------------------------------------------ */
                /*  Cart page busy state                                   */
                /*  Locks checkout + pulses summary during any mutation.   */
                /* ------------------------------------------------------ */

                var _busyTimer = null;

                function setCartBusy() {
                    if (_busyTimer) { clearTimeout(_busyTimer); }

                    // Pulse entire summary card (CSS blocks pointer-events)
                    var summary = document.querySelector('.cart-summary');
                    if (summary) { summary.classList.add('sm-cart-busy'); }

                    // Replace checkout button text with three dots
                    var btns = document.querySelectorAll(
                        '.cart-summary-button-proceed-checkout, .button-proceed-checkout'
                    );
                    for (var b = 0; b < btns.length; b++) {
                        if (!btns[b].getAttribute('data-original-text')) {
                            btns[b].setAttribute('data-original-text', btns[b].textContent.replace(/^\s+|\s+$/g, ''));
                        }
                        btns[b].innerHTML = '<span class="sm-dot"></span><span class="sm-dot"></span><span class="sm-dot"></span>';
                        btns[b].disabled = true;
                    }

                    // Failsafe: recover after 10s
                    _busyTimer = setTimeout(setCartReady, 10000);
                }

                function setCartReady() {
                    if (_busyTimer) { clearTimeout(_busyTimer); _busyTimer = null; }

                    // Restore summary card
                    var summary = document.querySelector('.cart-summary');
                    if (summary) { summary.classList.remove('sm-cart-busy'); }

                    // Restore checkout button text
                    var btns = document.querySelectorAll(
                        '.cart-summary-button-proceed-checkout, .button-proceed-checkout'
                    );
                    for (var b = 0; b < btns.length; b++) {
                        var original = btns[b].getAttribute('data-original-text') || 'Proceed to Checkout';
                        btns[b].textContent = original;
                        btns[b].disabled = false;
                        btns[b].removeAttribute('data-original-text');
                    }
                }

                // Hook all cart mutation events
                cart.on('beforeRemoveLine', setCartBusy);
                cart.on('afterRemoveLine', setCartReady);
                cart.on('beforeUpdateLine', setCartBusy);
                cart.on('afterUpdateLine', setCartReady);
                cart.on('beforeAddPromotion', setCartBusy);
                cart.on('afterAddPromotion', setCartReady);
            }

            /* ========================================================== */
            /*  LEGACY MODULES                                             */
            /*  Loaded as define() dependencies above — they self-execute  */
            /*  and apply prototype mutations. No further action needed.   */
            /*                                                             */
            /*  Includes:                                                  */
            /*  - Item.KeyMapping.Ext (thumbnail + compare price)          */
            /*  - Item.Model.Ext (getThumbnail override)                   */
            /*  - Product.Model.Ext (getImages override)                   */
            /*  - ProductLine.Common.Image.Ext (getThumbnail static)       */
            /*  - ProductDetails.ImageGallery.View.Ext (slider + pager)    */
            /* ========================================================== */

            /* ========================================================== */
            /*  NOTIFICATION AUTO-DISMISS                                  */
            /*  Success messages fade after 5s, info after 8s.            */
            /*  Error and warning persist until manually closed.           */
            /* ========================================================== */

            (function initNotificationAutoDismiss() {
                var SUCCESS_TIMEOUT = 5000;
                var INFO_TIMEOUT = 8000;
                var EXIT_DURATION = 300;

                function scheduleDismiss(el, delay) {
                    // Mark so we don't schedule twice
                    if (el.getAttribute('data-sm-dismiss')) { return; }
                    el.setAttribute('data-sm-dismiss', '1');

                    setTimeout(function() {
                        el.classList.add('sm-notify-exit');
                        setTimeout(function() {
                            if (el.parentNode) { el.parentNode.removeChild(el); }
                        }, EXIT_DURATION);
                    }, delay);
                }

                function processNode(node) {
                    if (node.nodeType !== 1) { return; }
                    var msgs = [];
                    if (node.classList && node.classList.contains('global-views-message')) {
                        msgs.push(node);
                    }
                    if (node.querySelectorAll) {
                        var found = node.querySelectorAll('.global-views-message');
                        for (var k = 0; k < found.length; k++) { msgs.push(found[k]); }
                    }
                    for (var m = 0; m < msgs.length; m++) {
                        if (msgs[m].classList.contains('global-views-message-success')) {
                            scheduleDismiss(msgs[m], SUCCESS_TIMEOUT);
                        } else if (msgs[m].classList.contains('global-views-message-info')) {
                            scheduleDismiss(msgs[m], INFO_TIMEOUT);
                        }
                    }
                }

                var observer = new MutationObserver(function(mutations) {
                    for (var i = 0; i < mutations.length; i++) {
                        var added = mutations[i].addedNodes;
                        for (var j = 0; j < added.length; j++) {
                            processNode(added[j]);
                        }
                    }
                });

                observer.observe(document.body, { childList: true, subtree: true });
            })();

            /* ========================================================== */
            /*  COPY MPN — clipboard button in meta zone                   */
            /*  Listens for clicks on [data-copy-mpn], copies value,       */
            /*  swaps button text to "COPIED!" for 1.2s then back.         */
            /* ========================================================== */

            document.body.addEventListener('click', function(e) {
                var btn = e.target.closest('[data-copy-mpn]');
                if (!btn) { return; }
                e.preventDefault();
                e.stopPropagation();
                var mpn = btn.getAttribute('data-copy-mpn');
                if (!mpn) { return; }

                function showCopied() {
                    btn.classList.add('is-copied');
                    setTimeout(function() { btn.classList.remove('is-copied'); }, 1200);
                }

                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(mpn).then(showCopied);
                } else {
                    // Fallback for older browsers / non-HTTPS
                    var ta = document.createElement('textarea');
                    ta.value = mpn;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    showCopied();
                }
            });

            /* ========================================================== */
            /*  KLAVIYO ONSITE TRACKING                                    */
            /*  Config-driven: speedmaster.KlaviyoEnabled (boolean)        */
            /*                 speedmaster.KlaviyoCompanyId (string)       */
            /*                                                             */
            /*  1. Injects base klaviyo.js async script + proxy object     */
            /*  2. Identifies logged-in customers on every navigation      */
            /*  3. Tracks "Viewed Product" on PDP                          */
            /* ========================================================== */

            var klaviyoEnabled = Configuration.get('speedmaster.KlaviyoEnabled');
            var klaviyoCompanyId = Configuration.get('speedmaster.KlaviyoCompanyId');

            // Only ever bootstrap Klaviyo in a REAL browser. SuiteCommerce's SEO
            // prerender runs this module with jsEnvironment !== 'browser'; if we
            // inject there, klaviyo.js loads and its dynamically-added chunk
            // <script> tags get SERIALIZED into the cached page HTML. The live
            // client then injects a second, newer Klaviyo build, and the two
            // webpack runtimes collide on the shared onsite-modules registry —
            // wedging the session (createClientSession never completes) so NO
            // events are ever sent. Never inject at prerender time.
            var isBrowser = !!(window.SC && SC.ENVIRONMENT && SC.ENVIRONMENT.jsEnvironment === 'browser');

            if (klaviyoEnabled && klaviyoCompanyId && isBrowser) {
                // --- 1. Inject base snippet (once) ---
                (function() {
                    // Async script loader — but never add a SECOND klaviyo.js if one
                    // is already on the page (e.g. a stale copy still baked into a
                    // cached/prerendered document). A duplicate bootstrap loads a
                    // second webpack build that collides with the first on the shared
                    // onsite registry and breaks tracking; the existing one keeps it.
                    if (!document.querySelector('script[src*="/onsite/js/' + klaviyoCompanyId + '/klaviyo.js"]')) {
                        var s = document.createElement('script');
                        s.type = 'text/javascript';
                        s.async = true;
                        s.src = 'https://static.klaviyo.com/onsite/js/' + klaviyoCompanyId + '/klaviyo.js?company_id=' + klaviyoCompanyId;
                        document.head.appendChild(s);
                    }

                    // Klaviyo proxy object — identical to their snippet
                    if (!window.klaviyo) {
                        window._klOnsite = window._klOnsite || [];
                        try {
                            window.klaviyo = new Proxy({}, {
                                get: function(n, i) {
                                    return 'push' === i
                                        ? function() { var n; (n = window._klOnsite).push.apply(n, arguments); }
                                        : function() {
                                            var n = arguments.length, o = new Array(n), w = 0;
                                            for (; w < n; w++) { o[w] = arguments[w]; }
                                            var t = 'function' == typeof o[o.length - 1] ? o.pop() : void 0;
                                            var e = new Promise(function(n) {
                                                window._klOnsite.push([i].concat(o, [function(i) { t && t(i); n(i); }]));
                                            });
                                            return e;
                                        };
                                }
                            });
                        } catch (e) {
                            window.klaviyo = window.klaviyo || [];
                            window.klaviyo.push = function() {
                                var n;
                                (n = window._klOnsite).push.apply(n, arguments);
                            };
                        }
                    }
                })();

                // --- 2. Identify logged-in customer on every page ---
                // SCS exposes profile via SC.ENVIRONMENT or the Profile model.
                // We check on every afterAppendView so login/logout transitions
                // are caught without a full page reload.
                layout.on('afterAppendView', function() {
                    try {
                        var env = SC && SC.ENVIRONMENT;
                        if (!env) { return; }
                        // SC.ENVIRONMENT.is_logged_in is the reliable flag
                        if (!env.is_logged_in || env.is_logged_in === 'F') { return; }
                        // Profile data lives at SC.ENVIRONMENT.currentTouchpoint context
                        // or on the Profile singleton. Try multiple paths.
                        var profile = env.PROFILE || env.profile || {};
                        var email = profile.email || profile.emailaddress || '';
                        if (!email) { return; }

                        window.klaviyo.push(['identify', {
                            '$email': email,
                            '$first_name': profile.firstname || '',
                            '$last_name': profile.lastname || '',
                            '$external_id': String(profile.internalid || '')
                        }]);
                    } catch (e) {
                        console.warn('SM Klaviyo identify error:', e);
                    }
                });

                // --- 3. Track "Viewed Product" on PDP ---
                if (pdp) {
                    pdp.on('afterShowContent', function() {
                        try {
                            var itemInfo = pdp.getItemInfo();
                            if (!itemInfo || !itemInfo.item) { return; }
                            var item = itemInfo.item;

                            // Build the Viewed Product payload
                            var payload = {
                                ProductName: item.storedisplayname2 || item.displayname || item._name || '',
                                ProductID: String(item.internalid || ''),
                                SKU: item.itemid || '',
                                ImageURL: item.itemimages_detail
                                    ? (function(imgs) {
                                        // First image URL from gallery
                                        try {
                                            var parsed = typeof imgs === 'string' ? JSON.parse(imgs) : imgs;
                                            var keys = Object.keys(parsed);
                                            if (keys.length) {
                                                var first = parsed[keys[0]];
                                                if (first.url) { return first.url; }
                                                var subkeys = Object.keys(first);
                                                if (subkeys.length && first[subkeys[0]].url) {
                                                    return first[subkeys[0]].url;
                                                }
                                            }
                                        } catch (e) { /* noop */ }
                                        return '';
                                    })(item.itemimages_detail)
                                    : '',
                                URL: window.location.href,
                                Brand: 'Speedmaster',
                                Categories: [item.commercecategory_name || item.classpath || '']
                            };

                            // Price — try multiple SC data paths
                            var priceDetail = item.onlinecustomerprice_detail;
                            if (priceDetail && priceDetail.onlinecustomerprice) {
                                payload.Price = parseFloat(priceDetail.onlinecustomerprice);
                            }

                            window.klaviyo.push(['track', 'Viewed Product', payload]);
                        } catch (e) {
                            console.warn('SM Klaviyo Viewed Product error:', e);
                        }
                    });
                }
            }
        }
    };
});
